import { type PropsWithChildren } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { T } from "@repo/transifex";
import { cn } from "~/lib/utils";
import { type StockStatus } from "~/utils/stock/infer-stock-status";
import { getAvailabilityCount } from "~/utils/stock/get-availability-count";

const badgeStyles = cva(
  "flex min-h-6 items-center rounded-xl bg-interface-neutral-6 px-2 py-0.5 text-center text-white",
  {
    variants: {
      size: {
        sm: "h-6 text-sm",
        md: "h-8 text-base",
        lg: "h-10 text-lg"
      },
      variant: {
        default: "bg-interface-neutral-6",
        danger: "bg-interface-negative-2"
      }
    },
    defaultVariants: {
      size: "sm",
      variant: "default"
    }
  }
);

type BadgeProps = PropsWithChildren<
  {
    className?: string;
  } & VariantProps<typeof badgeStyles>
>;

export const Badge = ({ children, className, size, variant }: BadgeProps) => (
  <div className={cn(badgeStyles({ size, variant }), className)}>
    {children}
  </div>
);

type BadgeStockProps = Pick<BadgeProps, "size"> & {
  stockStatus: StockStatus;
};

export const BadgeStock = ({ stockStatus, ...props }: BadgeStockProps) => {
  if (stockStatus === "out-of-stock") {
    return (
      <Badge variant="danger" {...props}>
        <T _str="Sold out" />
      </Badge>
    );
  }

  const count = getAvailabilityCount(stockStatus, null);
  if (count !== null) {
    return (
      <Badge {...props}>
        <T _str="{amount} left" amount={count} />
      </Badge>
    );
  }

  return null;
};
